import React, { useContext } from 'react';
import { Grid, MenuItem, Select, FormControl, FormHelperText, InputLabel, Chip, Box } from '@mui/material';
import { useFormikContext } from 'formik';
import { SelectChangeEvent } from '@mui/material/Select';
import DataContext from '../../../../context/DataProvider/DataContext';
import { AgentInputType } from '../../../../types/AgentInputType.types';
import DebounceTextField from '../../../../components/DebounceTextfield';
import { AgentStatus } from '../../../../types/Agent.types';
import Agentv2 from '../../../../types/Agentv2.types';
import AgentInit from '../../../../types/initValues/Agent.init';
import { Status } from '../../../../types/Status.types';
import { AGENT_ROLES_OPTIONS } from '../../../../services/config/CONST';
import PanelCard from './PanelCard';

function excludeStatus(statusArray: Status[]): Status[] {
  const statusToExclude = [AgentStatus.acquired, AgentStatus.restingCooperation, AgentStatus.interest];
  if (!statusArray) return [];
  return statusArray.filter((s) => !statusToExclude.includes(s.id));
}

function ContractPanel() {
  const { status, rank, level, brachOffices, contractType, agents } = useContext(DataContext);
  const { values, handleChange, errors, handleBlur, initialValues, setFieldValue } = useFormikContext<
    AgentInputType | Agentv2
  >();

  const isDisabled = initialValues.status_id === AgentStatus.dismissed || values.status_id === AgentStatus.failed;
  const getManager = agents.filter((agent) => agent.rank.is_manager);

  const handleRolesChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const newRoles = typeof value === 'string' ? value.split(',') : value;
    setFieldValue('roles', newRoles.length === 0 ? null : newRoles);
  };

  const handleDeleteRole = (roleToDelete: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const currentRoles = values.roles || [];
    const updatedRoles = currentRoles.filter((role) => role !== roleToDelete);
    setFieldValue('roles', updatedRoles.length === 0 ? null : updatedRoles);
  };

  return (
    <Box>
      <PanelCard title="Vertrag & Organisation" subtitle="Rang, Level, Firma, Vertragsart">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl
              disabled={
                values.agent_id === null ||
                initialValues.status_id !== AgentStatus.inProcess ||
                (values.status_id === AgentStatus.failed && values.agent_id !== 0)
              }
              fullWidth
              error={Boolean(errors.status_id) || false}
            >
              <InputLabel>Status</InputLabel>
              <Select
                name="status_id"
                value={values.status_id || ''}
                onChange={handleChange}
                label="Status"
                data-testid="status-select"
              >
                {excludeStatus(status)
                  ?.filter((item) => {
                    if (values.status_id !== AgentStatus.dismissed) {
                      return item.id !== AgentStatus.dismissed;
                    }
                    return item;
                  })
                  .map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.title}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>{errors.status_id}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <DebounceTextField
              label="Berater Nummer"
              aria-label="agent_id"
              name="agent_id"
              value={values.agent_id || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={errors.agent_id}
              error={Boolean(errors.agent_id) || false}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={Boolean(errors.rank_id) || false} disabled={isDisabled}>
              <InputLabel>Rang</InputLabel>
              <Select
                name="rank_id"
                value={values.rank_id || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                label="Rang"
                data-testid="rank_id-select"
              >
                {rank?.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.rank_id}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={Boolean(errors.level_id) || false} disabled={isDisabled}>
              <InputLabel>Level</InputLabel>
              <Select
                name="level_id"
                value={values.level_id || ''}
                onChange={handleChange}
                label="Level"
                data-testid="level_id-select"
              >
                {level?.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.level_id}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl
              disabled={values.agent_id !== AgentInit.agent_id}
              fullWidth
              error={Boolean(errors.branch_office_id) || false}
            >
              <InputLabel>Firma</InputLabel>
              <Select
                name="branch_office_id"
                value={values.branch_office_id || ''}
                onChange={handleChange}
                label="Firma"
                data-testid="branch_office_id-select"
              >
                {brachOffices?.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.branch_office_id}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={Boolean(errors.contract_type_id) || false} disabled={isDisabled}>
              <InputLabel>Vertragsart</InputLabel>
              <Select
                name="contract_type_id"
                value={values.contract_type_id || ''}
                onChange={handleChange}
                label="Vertragsart"
                data-testid="contract_type_id-select"
              >
                {contractType?.map((item) => (
                  <MenuItem key={item.id} value={item.id} disabled={!!(item.id === 3 && values.branch_office_id !== 1)}>
                    <span>{item.title}</span>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.contract_type_id}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={Boolean(errors.manager_id) || false} disabled={isDisabled}>
              <InputLabel>Teamleiter</InputLabel>
              <Select
                name="manager_id"
                value={values.manager_id || ''}
                onChange={handleChange}
                label="Teamleiter"
                data-testid="manager_id-select"
              >
                {getManager?.map((item) => (
                  <MenuItem key={item.id} value={item.id.toString()}>
                    {item.surname}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.manager_id}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <DebounceTextField
              type="date"
              label="Startdatum"
              name="start_date"
              aria-label="start_date"
              value={values.start_date || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={errors.start_date}
              error={Boolean(errors.start_date) || false}
              fullWidth
              disabled={isDisabled}
              inputProps={{ max: '2100-12-31' }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DebounceTextField
              label="Mandatsreferenz"
              name="mandateReference"
              value={values.mandateReference ?? ''}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={errors.mandateReference}
              error={Boolean(errors.mandateReference) || false}
              fullWidth
              disabled={isDisabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              error={Boolean(errors.roles) || false}
              disabled={
                initialValues.status_id === AgentStatus.dismissed ||
                values.status_id === AgentStatus.failed ||
                values.status_id === AgentStatus.inProcess
              }
            >
              <InputLabel>Rollen</InputLabel>
              <Select
                label="Rollen"
                name="roles"
                multiple
                value={values.roles || []}
                onChange={handleRolesChange}
                onBlur={handleBlur}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip
                        key={value}
                        label={AGENT_ROLES_OPTIONS.find((opt) => opt.value === value)?.label || value}
                        size="small"
                        onDelete={(event) => handleDeleteRole(value, event)}
                        deleteIcon={
                          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                          <span
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            ×
                          </span>
                        }
                      />
                    ))}
                  </Box>
                )}
              >
                {AGENT_ROLES_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.roles}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </PanelCard>
    </Box>
  );
}

export default ContractPanel;
